/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, isEqual, mapValues, set } from 'lodash';

import reportsConfig from '../configs/reports-config';
import InlineQuery from '../../components/InlineQuery';

import Iframe from '../../components/Iframe';
import PageHeader from '../../components/PageHeader';
import { TabbedFilters, FilterTypes } from '../../components/Filters';
import Table from '../../components/Table';
import TitleBanner from '../../components/TitleBanner';
import {
  extractFilterValues,
  getFieldDefaultSingleDateFilter
} from '../../utils/filters';
import { columns, formatMovies, defaultSorts } from './table-config';
import { SINGLE_DATE_FILTER_ID } from '../../components/Filters/Filter/SingleDateFilter';
import { getSingleDate } from '../../utils/dates';
import { MIN_DATE, MAX_DATE, getDaysArray } from '../../config/filterOptions';

const AnticipationReport = ({
  filters,
  queries,
  datesLookupsQuery,
  queryActions: { runInlineQueriesForPage, resetInlineQueryResultsForPage },
  filtersActions: { getFiltersForPageAsync, updateFilters },
  useDatesLookup,
  blocked
}) => {
  const [selectedFilters, setSelectedFilters] = useState({});
  // There is a case when we don't send filter to the service but a default value for it to show in TabbedFilter
  const [labelFields, setLabelFields] = useState({});
  const { loading: isDatesLoading, data: dates = [] } = useDatesLookup
    ? datesLookupsQuery.dateLookupAnticipation
    : { loading: false, data: getDaysArray(MIN_DATE, MAX_DATE) };

  const fetchDateLookupAnticipation = () => {
    runInlineQueriesForPage(reportsConfig.DATE_LOOKUP_ANTICIPATION);
  };

  const updateQueries = fields => {
    runInlineQueriesForPage(
      reportsConfig.ANTICIPATION_MOVIE_REPORT_FILTERS,
      fields
    );
  };
  const didMountRef = useRef(false);

  useEffect(() => {
    if (!blocked && !isDatesLoading) {
      const fields = filters.fields;
      const values = extractFilterValues(fields);
      const hasNewValues = !isEqual(values, selectedFilters);
      if (hasNewValues && filters.groupings.length && didMountRef.current) {
        const futureReleaseDateFieldValue = get(
          fields,
          'future_release_date.value'
        );
        const singleDateFieldValue = get(
          fields,
          `${SINGLE_DATE_FILTER_ID}.value`
        );
        const defaultDateFieldValue = getSingleDate(dates[dates.length - 1]);
        // Reset date filter if it equal to default and no future_release_date;
        if (
          !futureReleaseDateFieldValue &&
          isEqual(singleDateFieldValue, defaultDateFieldValue)
        ) {
          set(fields, `${SINGLE_DATE_FILTER_ID}.value`, null);
        }
        const defaultSingleDateField = getFieldDefaultSingleDateFilter(
          fields,
          dates
        );
        if (!isEmpty(defaultSingleDateField)) {
          setLabelFields(defaultSingleDateField);
        }

        setSelectedFilters(values);
        updateQueries(fields);
        const filters = mapValues(fields, 'value');
        const booleanFilters = mapValues(fields, 'booleanSeparator');
        updateFilters(filters, { syncWithUrl: true }, booleanFilters);
      }
    }
  }, [blocked, filters.fields, dates]);

  useEffect(() => {
    getFiltersForPageAsync(reportsConfig.ANTICIPATION_MOVIE_REPORT_PAGE_ID);
    document.title = 'Anticipation | Movies | CVM Insights';
    didMountRef.current = true;
    if (useDatesLookup) {
      fetchDateLookupAnticipation();
    }
    return () => {
      resetInlineQueryResultsForPage(
        reportsConfig.ANTICIPATION_MOVIE_REPORT_FILTERS
      );
    };
  }, []);

  return (
    <>
      <TitleBanner>
        <PageHeader title="Anticipated Movies" />
      </TitleBanner>

      {blocked && (
        <Iframe
          blocked
          blockedMessage="Contact your Client Success Representative to learn more about this report"
          url=""
          height="100%"
        />
      )}

      {!blocked && (
        <>
          <TabbedFilters dates={dates} labelFields={labelFields} />
          <section>
            <InlineQuery useChildForLoading query={queries.moviesAnticipation}>
              {query => (
                <Table
                  data={formatMovies(query)}
                  headerFormatter={columns}
                  defaultSorts={defaultSorts}
                  view="moviesAnticipation"
                  filters={filters.fields}
                  queryMeta={
                    reportsConfig.ANTICIPATION_MOVIE_REPORT_FILTERS.queries
                      .moviesAnticipation
                  }
                />
              )}
            </InlineQuery>
          </section>
        </>
      )}
    </>
  );
};

AnticipationReport.propTypes = {
  blocked: PropTypes.bool.isRequired,

  filters: PropTypes.shape({
    fields: FilterTypes.FilterSetType, // eslint-disable-line react/no-typos
    groupings: FilterTypes.GroupingSetType // eslint-disable-line react/no-typos
  }).isRequired,

  filtersActions: PropTypes.shape({
    getFiltersForPageAsync: PropTypes.func.isRequired
  }).isRequired,

  queryActions: PropTypes.shape({
    runInlineQueriesForPage: PropTypes.func.isRequired
  }).isRequired,

  queries: PropTypes.shape({
    moviesAnticipation: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      loading: PropTypes.bool.isRequired
    })
  }).isRequired,

  datesLookupsQuery: PropTypes.shape({
    dateLookupAnticipation: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      loading: PropTypes.bool.isRequired
    })
  }).isRequired
};

export default AnticipationReport;
